/** @graphql agg **/
import { useCallback, useEffect, useState } from 'react';
import { fetchQuery, graphql, useFragment, type GraphQLTaggedNode } from 'react-relay';
import { createOperationDescriptor, getRequest } from 'relay-runtime';

import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useExternalLabelsQuery,
	useExternalLabelsQuery$data,
} from './__generated__/useExternalLabelsQuery.graphql.ts';
import type {
	useExternalLabels_query$data,
	useExternalLabels_query$key,
} from './__generated__/useExternalLabels_query.graphql.ts';

/**
 * Standalone Relay read of managed (external) labels applied to content, gated behind
 * `confluence_managed_external_labels_endpoint`. Used by the legacy picker
 * (`useLabelsSelectProps`); View Page surfaces read via the `useExternalLabels_query`
 * fragment instead. Uses `fetchQuery` (not suspending `useLazyLoadQuery`) to keep the
 * imperative `{ labels, loading, error, refetch }` contract and self-skip when gated off.
 *
 * Store-subscribed: subscribes to the normalized `confluence_externalLabels` record so
 * mutation-updater writes re-render the picker instantly (replacing the optimistic overlay).
 * Do NOT refetch after a mutation — the store write is authoritative, and a lagging replica
 * could clobber it and reintroduce the flicker bug.
 */
export const EXTERNAL_LABELS_QUERY: GraphQLTaggedNode = graphql`
	query useExternalLabelsQuery($cloudId: ID!, $id: ID!) {
		confluence_externalLabels(cloudId: $cloudId, id: $id) {
			id
			kind
			name
		}
	}
`;

/**
 * Colocated fragment on `Query` for the managed labels field, spread into the View
 * Page `ContentUnifiedQuery` (warmed into the store by route preload) and each
 * surface's own query, then read via `useFragment`. The spreading operation gates
 * it via `@include(if: $includeExternalLabels)`; when excluded the ref is absent
 * and the read returns an empty list.
 */
export const EXTERNAL_LABELS_FRAGMENT: GraphQLTaggedNode = graphql`
	fragment useExternalLabels_query on Query
	@argumentDefinitions(cloudId: { type: "ID!" }, id: { type: "ID!" }) {
		confluence_externalLabels(cloudId: $cloudId, id: $id) {
			id
			kind
			name
		}
	}
`;

export type ExternalLabel = NonNullable<
	NonNullable<useExternalLabelsQuery$data['confluence_externalLabels']>[number]
>;

export type UseExternalLabelsResult = {
	labels: readonly ExternalLabel[];
	loading: boolean;
	error: Error | undefined;
	refetch: () => void;
};

// Single state object so each transition is one atomic update.
type ExternalLabelsState = {
	labels: readonly ExternalLabel[];
	loading: boolean;
	error: Error | undefined;
};

const SETTLED_EMPTY: ExternalLabelsState = { labels: [], loading: false, error: undefined };

// Builds the content ARI. Exported so the preloader and each consumer build the same
// `confluence_externalLabels` args. Fails closed (empty ARI) for any type that isn't an
// eligible page/blogpost, so callers skip rather than point a mutation at the wrong entity.
export const buildExternalLabelsAri = (
	cloudId: string,
	contentId: string,
	contentType?: string | null,
): string => {
	if (!cloudId || !contentId || (contentType !== 'page' && contentType !== 'blogpost')) {
		return '';
	}
	return `ari:cloud:confluence:${cloudId}:${contentType}/${contentId}`;
};

export const useExternalLabels = (
	contentId: string,
	contentType?: string | null,
): UseExternalLabelsResult => {
	const { cloudId } = useSessionData();
	const entityAri = buildExternalLabelsAri(cloudId, contentId, contentType);

	const skip =
		!fg('confluence_managed_external_labels_endpoint') || !cloudId || !contentId || !entityAri;

	// Start loading when active so consumers don't flash empty before the first read.
	const [state, setState] = useState<ExternalLabelsState>(() =>
		skip ? SETTLED_EMPTY : { labels: [], loading: true, error: undefined },
	);

	// Bumping this re-runs the effect, giving `refetch` a network round-trip.
	const [reloadToken, setReloadToken] = useState(0);

	const runQuery = useCallback(() => {
		if (skip) {
			setState(SETTLED_EMPTY);
			return () => {};
		}

		setState((prev) => ({ ...prev, loading: true, error: undefined }));

		const environment = getRelayEnvironment();
		const operation = createOperationDescriptor(getRequest(EXTERNAL_LABELS_QUERY), {
			cloudId,
			id: entityAri,
		});

		// Retain so the subscribed records aren't GC'd while mounted.
		const retention = environment.retain(operation);

		let cancelled = false;

		// Re-read on any change to the normalized record (incl. mutation-updater writes).
		const publishFromStore = (data: useExternalLabelsQuery$data | null | undefined) => {
			if (cancelled) {
				return;
			}
			setState((prev) => ({
				// Gateway types array and elements as nullable; drop nulls.
				labels: (data?.confluence_externalLabels ?? []).filter(
					(label): label is ExternalLabel => label != null,
				),
				loading: false,
				error: prev.error,
			}));
		};

		const storeSubscription = environment.subscribe(
			environment.lookup(operation.fragment),
			(snapshot) => publishFromStore(snapshot.data as useExternalLabelsQuery$data | undefined),
		);

		const fetchSubscription = fetchQuery<useExternalLabelsQuery>(
			environment,
			EXTERNAL_LABELS_QUERY,
			{ cloudId, id: entityAri },
			{
				// Store-first (warmed by SSR `preloadManagedLabels`), network otherwise.
				// No `X-Query-Context` header so the field folds into the batched ContentUnifiedQuery.
				fetchPolicy: 'store-or-network',
			},
		).subscribe({
			next: (data) => publishFromStore(data),
			error: (err: unknown) => {
				if (cancelled) {
					return;
				}
				setState((prev) => ({
					...prev,
					loading: false,
					error: err instanceof Error ? err : new Error(String(err)),
				}));
			},
		});

		return () => {
			cancelled = true;
			fetchSubscription.unsubscribe();
			storeSubscription.dispose();
			retention.dispose();
		};
	}, [skip, cloudId, entityAri]);

	useEffect(() => {
		// `runQuery` returns its cleanup; `reloadToken` re-runs it for `refetch`.
		return runQuery();
	}, [runQuery, reloadToken]);

	return {
		labels: state.labels,
		loading: state.loading,
		error: state.error,
		refetch: () => {
			if (!skip) {
				setReloadToken((token) => token + 1);
			}
		},
	};
};

/**
 * Reads managed (external) labels from a `useExternalLabels_query` fragment ref
 * exposed by a parent operation. Pure store read — loading/error belong to the
 * parent query. The gate lives on the spreading operation, so an absent ref yields
 * an empty list; callers still gate the managed-label UI inline.
 */
export const useExternalLabelsFragment = (
	fragmentRef: useExternalLabels_query$key | null | undefined,
): readonly ExternalLabel[] => {
	// Native `useFragment` calls `useRelayEnvironment()` even for a null ref, so it
	// throws when no provider is mounted (Apollo mode / Apollo-mode jest tests). The
	// spread only exists when the gate is on, so skip the read entirely when it's off.
	// The gate is session-stable within a render tree, matching `useReadFragmentInterop`.
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	const data: useExternalLabels_query$data | null | undefined = fg(
		'confluence_managed_external_labels_endpoint',
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- gate is session-stable within a render tree, matching useReadFragmentInterop
			useFragment(EXTERNAL_LABELS_FRAGMENT, fragmentRef ?? null)
		: null;

	// Gateway types array and elements as nullable; drop nulls.
	return (data?.confluence_externalLabels ?? []).filter(
		(label): label is ExternalLabel => label != null,
	);
};

export type ExternalLabelsFragmentVariables = {
	includeExternalLabels: boolean;
	cloudId: string;
	id: string;
};

/**
 * Builds the three `useExternalLabels_query` spread variables, deriving
 * `includeExternalLabels` from the gate. When excluded, `cloudId`/`id` are empty and the
 * `@include`-gated spread is skipped.
 */
export const useExternalLabelsFragmentVariables = (
	contentId: string,
	contentType?: string | null,
): ExternalLabelsFragmentVariables => {
	const { cloudId } = useSessionData();
	const entityAri = buildExternalLabelsAri(cloudId, contentId, contentType);
	// Empty ARI (unresolved content type) fails closed: don't include the spread.
	const includeExternalLabels =
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		Boolean(entityAri) && fg('confluence_managed_external_labels_endpoint');

	return {
		includeExternalLabels,
		cloudId: includeExternalLabels ? cloudId : '',
		id: includeExternalLabels ? entityAri : '',
	};
};
